<script setup>
import { useAuthorizationStore } from '@/stores/useAuthStore.js'
import { useModulesStore } from '@/stores/useModulesStore.js'
import { usePermissionsStore } from '@/stores/usePermissionsStore.js'
import FlyoutMenu from '@elements/FlyoutMenu.vue'
import MenuItem from '@elements/MenuItem.vue'
import UserMenu from '@elements/UserMenu.vue'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

defineProps({
	activeModule: { type: String, default: 'Home' }
})

const APP_NAME = import.meta.env.VITE_APP_NAME
const auth = useAuthorizationStore()
const i18n = useI18n()
const MODE = import.meta.env.MODE
const modules = useModulesStore()
const permissions = usePermissionsStore()

const showMenu = ref(false)
const user = auth.user
const roles = user?.rolesByName ?? []

const canSeeSuppliers = computed(() => {
	return (
		modules.isEnabled('sensory')
		|| modules.isEnabled('innovativefresh')
	)
		&& permissions.has('settings.suppliers')
})
</script>

<template>
	<header
		:class="showMenu ? 'show-menu': 'hide-menu'"
		class="nfo-header bg-system-secondary"
	>
		<div class="nfo-header-grid container">
			<div class="nfo-menu-toggler">
				<button
					class="text-primary fw-bold btn-none"
					type="button"
					@click="showMenu = !showMenu"
				>
					<font-awesome-icon
						:class="showMenu ? 'fa-rotate-90' : 'fa-rotate-0'"
						icon="bars"
					/>
					Menu
				</button>
			</div>

			<div class="nfo-logo">
				<router-link
					:to="{ name: 'app.start' }"
					class="nfo-logo-link"
				>
					<img
						v-if="MODE === 'development'"
						:alt="`${APP_NAME} logo`"
						class="nfo-logo-full"
						height="67"
						loading="lazy"
						src="@/assets/nfo-logo-local.svg"
						width="298"
					>
					<img
						v-if="MODE !== 'development'"
						:alt="`${APP_NAME} logo`"
						class="nfo-logo-full"
						height="67"
						loading="lazy"
						src="@/assets/nfo-logo.svg"
						width="298"
					>
					<img
						v-if="MODE === 'development'"
						:alt="`${APP_NAME} griddy`"
						class="nfo-logo-grid"
						height="100"
						loading="lazy"
						src="@/assets/nfo-griddy-local.svg"
						width="100"
					>
					<img
						v-if="MODE !== 'development'"
						:alt="`${APP_NAME} griddy`"
						class="nfo-logo-grid"
						height="100"
						loading="lazy"
						src="@/assets/nfo-griddy.svg"
						width="100"
					>
				</router-link>
			</div>

			<div
				v-if="!auth.isOfUserType('supplier')"
				class="nfo-menu-modules"
			>
				<FlyoutMenu
					btn-class="btn btn-light justify-content-between"
					position="bottom-start"
				>
					<template #button>
						<portal-target name="active-module">
							Home
						</portal-target>
						<font-awesome-icon icon="chevron-down" />
					</template>
					<template #items>
						<MenuItem :route="{ name: 'app.start' }">
							{{ i18n.t('modules.dashboard') }}
						</MenuItem>
						<MenuItem
							v-if="modules.isEnabled('audits') && permissions.has('audits')"
							:route="{ name: 'audits' }"
						>
							{{ i18n.t('modules.audits') }}
						</MenuItem>
						<MenuItem
							v-if="modules.isEnabled('innovativefresh') && permissions.has('innovativefresh')"
							:route="{ name: 'innovativeFresh' }"
						>
							{{ i18n.t('modules.innovativefresh') }}
						</MenuItem>
						<MenuItem
							v-if="modules.isEnabled('lab') && permissions.has('lab')"
							:route="{ name: 'lab' }"
						>
							{{ i18n.t('modules.lab') }}
						</MenuItem>
						<MenuItem
							v-if="modules.isEnabled('manuals') && permissions.has('manuals')"
							:route="{ name: 'modules.manuals' }"
						>
							{{ i18n.t('modules.manuals') }}
						</MenuItem>
						<MenuItem
							v-if="modules.isEnabled('registrations') && permissions.has('registrations')"
							:route="{ name: 'module.registrations' }"
						>
							{{ i18n.t('modules.registrations') }}
						</MenuItem>

						<MenuItem
							v-if="modules.isEnabled('sensory') && permissions.has('sensory')"
							:route="{ name: 'modules.sensory' }"
						>
							{{ i18n.t('modules.sensory') }}
						</MenuItem>
						<MenuItem
							v-if="modules.isEnabled('suppliers') && permissions.has('suppliers')"
							:route="{ name: 'module.suppliers' }"
						>
							{{ i18n.t('modules.suppliers') }}
						</MenuItem>
						<li
							role="separator"
						/>
						<MenuItem
							v-if="roles.includes('Systeem beheerder')"
							:route="{ name: 'admin' }"
						>
							{{ i18n.t('modules.admin') }}
						</MenuItem>
						<MenuItem
							v-if="auth.isOfUserType('employee') && permissions.has('settings')"
							:route="{ name: 'settings' }"
						>
							{{ i18n.t('modules.settings') }}
						</MenuItem>
						<MenuItem
							v-if="auth.isOfUserType('employee') && permissions.has(
								'settings.divisions.divisions',
								'settings.locations.locations',
								'settings.departments.departments',
								'settings.users.users'
							)"
							:route="{ name: 'company' }"
						>
							{{ i18n.t('modules.companyStructure') }}
						</MenuItem>
						<MenuItem
							v-if="canSeeSuppliers"
							:route="{ name: 'module.suppliers-index' }"
						>
							{{ i18n.t('common.suppliers', 0) }}
						</MenuItem>
					</template>
				</FlyoutMenu>
			</div>

			<nav class="nfo-menu-module">
				<ul><portal-target name="submenu" /></ul>
			</nav>

			<nav class="nfo-menu-user">
				<UserMenu />
			</nav>
		</div>
	</header>
</template>
